import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Box,
  useTheme,
  useMediaQuery,
  CircularProgress, // <-- Add this
  Typography,       // <-- Add this
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import axios from "axios"; // Import axios for API calls

const TableComponent = ({ setPopupWarning, setWarningProducts }) => {
  const [search, setSearch] = useState("");
  const [data, setData] = useState([]); // State to store product data
  const [loading, setLoading] = useState(true); // <-- Add loading state

  // Responsive hooks
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Fetch product data from the backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get("https://enzo-matr.onrender.com/api/products"); // Ensure the URL matches the backend
        if (response.data && response.data.length) {
          setData(response.data);

          // Filter products for PopupWarning (count < 50)
          const popupWarningProducts = response.data.filter((product) => product.count < 50);
          setPopupWarning(popupWarningProducts);

          // Filter products for warningProducts (count < 10)
          const warningProducts = response.data.filter((product) => product.count < 10);
          setWarningProducts(warningProducts);
        } else {
          console.warn("No data received from the API.");
        }
      } catch (error) {
        console.error("Error fetching product data:", error);
      } finally {
        setLoading(false); // <-- Set loading to false after fetch
      }
    };
    fetchData();
  }, [setPopupWarning, setWarningProducts]);

  const filteredData = data.filter((row) =>
    Object.values(row).some((val) => val.toString().toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <Paper
      sx={{
        width: isMobile ? "98vw" : isTablet ? "91vw" : "730px",
        height: isMobile ? "auto" : "380px",
        marginLeft: isMobile ? 0 : isTablet ? "10px" : "20px",
        padding: isMobile ? "8px" : "15px",
        mt: 1,
        borderRadius: "10px",
        borderWidth: 1,
        boxShadow: 1,
        mx: isMobile ? "auto" : undefined,
      }}
    >
      {/* Search Bar with External Search Icon */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
          gap: 1,
          marginBottom: "10px",
        }}
      >
        <TextField
          sx={{
            "& .MuiOutlinedInput-root": {
              height: isMobile ? "40px" : "50px",
              borderRadius: 4,
              fontFamily: "bricolage grotesque",
              fontSize: isMobile ? "14px" : "16px",
            },
          }}
          fullWidth
          variant="outlined"
          placeholder="Search Item, Month, Date, Price etc."
          onChange={(e) => setSearch(e.target.value)}
        />
        <IconButton
          sx={{
            backgroundColor: "#333",
            width: isMobile ? "40px" : "50px",
            height: isMobile ? "40px" : "50px",
            color: "#fff",
            borderRadius: "10px",
            "&:hover": { backgroundColor: "#555" },
            alignSelf: isMobile ? "flex-end" : "center",
          }}
        >
          <SearchIcon />
        </IconButton>
      </Box>

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: "12px",
          maxHeight: isMobile ? 220 : 330, // Reduce height for mobile
          overflow: "auto",
          "&::-webkit-scrollbar": { display: "none" }, // Hides scrollbar for WebKit browsers
          "-ms-overflow-style": "none", // Hides scrollbar for IE and Edge
          "scrollbar-width": "none", // Hides scrollbar for Firefox
        }}
      >
        <Table stickyHeader size={isMobile ? "small" : "medium"}>
          {/* Table Header */}
          <TableHead>
            <TableRow sx={{ backgroundColor: "#333" }}>
              {["Name", "Company", "Category", "Count", "VAT", "Price", "State"].map((header) => (
                <TableCell
                  key={header}
                  sx={{
                    color: "#fff",
                    fontSize: isMobile ? "14px" : "18px",
                    fontWeight: "bold",
                    fontFamily: "bricolage grotesque",
                    position: "sticky",
                    top: 0,
                    backgroundColor: "#333",
                    borderBottom: "2px solid #555", // Adds a bottom border
                    px: isMobile ? 1 : 2,
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          {/* Scrollable Table Body */}
          <TableBody>
            {filteredData.map((row, index) => (
              <TableRow key={index}>
                <TableCell sx={{ fontFamily: "bricolage grotesque", fontWeight: "bold", fontSize: isMobile ? "12px" : "16px", px: isMobile ? 1 : 2 }}>
                  {row.name}
                </TableCell>
                <TableCell sx={{ fontFamily: "bricolage grotesque", fontWeight: "bold", fontSize: isMobile ? "12px" : "16px", px: isMobile ? 1 : 2 }}>
                  {row.company}
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", fontSize: isMobile ? "12px" : "16px", px: isMobile ? 1 : 2 }}>{row.category}</TableCell>
                <TableCell
                  sx={{
                    fontFamily: "bricolage grotesque",
                    fontWeight: "bold",
                    fontSize: isMobile ? "12px" : "16px",
                    px: isMobile ? 1 : 2,
                    color: row.count <= row.previous ? "red" : "inherit", // <-- Add this line
                  }}
                >
                  {row.count}
                </TableCell>
                <TableCell sx={{ fontFamily: "bricolage grotesque", fontWeight: "bold", fontSize: isMobile ? "12px" : "16px", px: isMobile ? 1 : 2 }}>
                  {row.vat}
                </TableCell>
                <TableCell sx={{ fontFamily: "bricolage grotesque", fontWeight: "bold", fontSize: isMobile ? "12px" : "16px", px: isMobile ? 1 : 2 }}>
                  {row.price}
                </TableCell>
                <TableCell sx={{ fontFamily: "bricolage grotesque", fontWeight: "bold", fontSize: isMobile ? "12px" : "16px", px: isMobile ? 1 : 2 }}>
                  {row.state}
                </TableCell>
              </TableRow>
            ))}
            {/* Loading indicator below data */}
            {loading && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <CircularProgress size={40} color="primary" />
                    <Typography sx={{ mt: 2, color: "#333", fontWeight: "bold" }}>loading......</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default TableComponent;